import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { fetchUserProfile, updateUserPassword, requestToBecomePartner } from '../../api/profile';
import LoadingSpinner from '../common/Loader';
import { Button } from '../common/Button';

const Settings = () => {
    const { user } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [profileData, setProfileData] = useState({
        first_name: '',
        last_name: '',
        username: '',
        email: '',
        country: ''
    });
    const [loading, setLoading] = useState({
        profile: true,
        password: false,
        partnerRequest: false
    });
    const [errors, setErrors] = useState({
        profile: null,
        password: null,
        partnerRequest: null
    });
    const [success, setSuccess] = useState(null);
    const [partnerSuccess, setPartnerSuccess] = useState(null);

    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                const response = await fetchUserProfile(user.user_id);
                if (response.status === 200 && response.user) {
                    setProfileData({
                        first_name: response.user.first_name || '',
                        last_name: response.user.last_name || '',
                        username: response.user.username || user.username || '',
                        email: response.user.email || user.email || '',
                        country: response.user.Country || ''
                    });
                } else {
                    throw new Error('Invalid profile data received');
                }
            } catch (error) {
                setErrors(prev => ({
                    ...prev,
                    profile: error.message || 'Failed to load profile'
                }));
            } finally {
                setLoading(prev => ({ ...prev, profile: false }));
            }
        };

        if (user?.user_id) {
            loadUserProfile();
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            setErrors(prev => ({
                ...prev,
                password: 'New passwords do not match'
            }));
            return;
        }

        try {
            setLoading(prev => ({ ...prev, password: true }));
            setErrors(prev => ({ ...prev, password: null }));
            setSuccess(null);

            await updateUserPassword(
                user.user_id,
                formData.currentPassword,
                formData.newPassword
            );

            setSuccess('Password updated successfully');
            setFormData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
        } catch (error) {
            setErrors(prev => ({
                ...prev,
                password: error.response?.data?.message ||
                    'Failed to update password'
            }));
        } finally {
            setLoading(prev => ({ ...prev, password: false }));
        }
    };

    const handlePartnerRequest = async () => {
        if (!profileData.country) {
            setErrors(prev => ({
                ...prev,
                partnerRequest: 'Country information is required to become a partner'
            }));
            return;
        }

        try {
            setLoading(prev => ({ ...prev, partnerRequest: true }));
            setErrors(prev => ({ ...prev, partnerRequest: null }));
            setPartnerSuccess(null);

            await requestToBecomePartner(user.user_id, profileData.country);

            setPartnerSuccess('Partner request submitted successfully! Our team will review your application shortly.');
        } catch (error) {
            setErrors(prev => ({
                ...prev,
                partnerRequest: error.message || 'Failed to submit partner request'
            }));
        } finally {
            setLoading(prev => ({ ...prev, partnerRequest: false }));
        }
    };

    if (loading.profile) {
        return (
            <div className="p-6">
                <LoadingSpinner />
            </div>
        );
    }

    if (errors.profile) {
        return (
            <div className="p-6">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                    {errors.profile}
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold mb-6">Settings</h1>

            {/* Profile Information Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Profile Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            First Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                            {profileData.first_name || 'Not specified'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Last Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                            {profileData.last_name || 'Not specified'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Email
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                            {profileData.email || 'Not specified'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Username
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                            {profileData.username || 'Not specified'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Country
                        </label>
                        <p className="mt-1 text-sm text-gray-900">
                            {profileData.country || 'Not specified'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Become Partner Section */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Become a Partner</h2>
                {partnerSuccess && (
                    <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                        {partnerSuccess}
                    </div>
                )}
                {errors.partnerRequest && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {errors.partnerRequest}
                    </div>
                )}
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-sm text-gray-600 mb-2">
                            Request to become a partner and start earning commissions by referring clients to our platform.
                        </p>
                        
                    </div>
                    <Button
                        onClick={handlePartnerRequest}
                        disabled={loading.partnerRequest || !profileData.country}
                        width='w-48'
                        className={`${loading.partnerRequest || !profileData.country ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {loading.partnerRequest ? 'Processing...' : 'Become a Partner'}
                    </Button>
                </div>
            </div>

            {/* Change Password Section */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Change Password</h2>
                {success && (
                    <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
                        {success}
                    </div>
                )}
                {errors.password && (
                    <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {errors.password}
                    </div>
                )}
                <form onSubmit={handlePasswordChange}>
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label
                                htmlFor="currentPassword"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Current Password
                            </label>
                            <input
                                type="password"
                                id="currentPassword"
                                name="currentPassword"
                                value={formData.currentPassword}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                                minLength="6"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="newPassword"
                                className="block text-sm font-medium text-gray-700"
                            >
                                New Password
                            </label>
                            <input
                                type="password"
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                                minLength="8"
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="confirmPassword"
                                className="block text-sm font-medium text-gray-700"
                            >
                                Confirm New Password
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                required
                                minLength="8"
                            />
                        </div>
                        <div>
                            <Button
                                type="submit"
                                disabled={loading.password}
                                width='w-48'
                                className={`${loading.password ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {loading.password ? 'Updating...' : 'Change Password'}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Settings;